import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import ConfirmModal from "../components/ConfirmModal";
import AdminMetrics from "../components/admin/AdminMetrics";
import LoopControl from "../components/admin/LoopControl";
import UsersPanel from "../components/admin/UsersPanel";
import ChipsMonitor from "../components/admin/ChipsMonitor";
import PlansEditor from "../components/admin/PlansEditor";
import GruposPanel from "../components/admin/GruposPanel";
import LogsPanel from "../components/admin/LogsPanel";
import GrantModal from "../components/admin/GrantModal";
import { PLAN_OPTIONS } from "../components/admin/constants";
import { toast } from "sonner";

export default function Admin() {
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [userSearch, setUserSearch] = useState("");
  const [appliedUserSearch, setAppliedUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [chips, setChips] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [chipSearch, setChipSearch] = useState("");
  const [appliedChipSearch, setAppliedChipSearch] = useState("");
  const [chipStatus, setChipStatus] = useState("todos");
  const [chipPage, setChipPage] = useState(1);
  const [allClients, setAllClients] = useState([]);
  const [logs, setLogs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planDrafts, setPlanDrafts] = useState({});
  const [savingPlan, setSavingPlan] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [grupoNome, setGrupoNome] = useState("");
  const [grupoLink, setGrupoLink] = useState("");
  const [addingGrupo, setAddingGrupo] = useState(false);
  const [loop, setLoop] = useState(null);
  const [savingLoop, setSavingLoop] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [grantUser, setGrantUser] = useState(null);
  const [grantPlan, setGrantPlan] = useState("m5");
  const [grantDays, setGrantDays] = useState("7");
  const [granting, setGranting] = useState(false);

  const fetchMetricsLogs = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([api.get("/admin/metrics"), api.get("/admin/logs")]);
      setMetrics(m.data);
      setLogs(l.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  const fetchUsers = useCallback(async (page = 1, search = "") => {
    try {
      const { data } = await api.get("/admin/users", { params: { page, limit: 10, search } });
      setUsers(data);
    } catch (e) {
      console.error("Falha ao carregar usuários", e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao carregar usuários");
    }
  }, []);

  const fetchChips = useCallback(async (page = 1, search = "", status = "todos") => {
    try {
      const { data } = await api.get("/admin/chips", {
        params: { page, limit: 10, search, status: status === "todos" ? "" : status },
      });
      setChips(data);
    } catch (e) {
      console.error("Falha ao carregar chips", e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao carregar chips");
    }
  }, []);

  const fetchStatic = useCallback(async () => {
    try {
      const [p, g, lp, uc] = await Promise.all([
        api.get("/payments/plans"), api.get("/admin/grupos"), api.get("/admin/loop"),
        api.get("/admin/users", { params: { page: 1, limit: 50 } }),
      ]);
      setPlans(p.data);
      const drafts = {};
      p.data.forEach((pl) => { drafts[pl.key] = { price: pl.price, chip_limit: pl.chip_limit }; });
      setPlanDrafts(drafts);
      setGrupos(g.data);
      setLoop(lp.data);
      setAllClients(uc.data.items.filter((u) => u.role !== "admin"));
    } catch (e) {
      console.error("Falha ao carregar configurações administrativas", e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao carregar configurações administrativas");
    }
  }, []);

  useEffect(() => {
    fetchMetricsLogs();
    fetchStatic();
    const t = setInterval(fetchMetricsLogs, 15000);
    return () => clearInterval(t);
  }, [fetchMetricsLogs, fetchStatic]);

  useEffect(() => { fetchUsers(userPage, appliedUserSearch); }, [userPage, appliedUserSearch, fetchUsers]);
  useEffect(() => { fetchChips(chipPage, appliedChipSearch, chipStatus); }, [chipPage, appliedChipSearch, chipStatus, fetchChips]);

  const submitUserSearch = useCallback(() => {
    setUserPage(1);
    setAppliedUserSearch(userSearch);
  }, [userSearch]);

  const submitChipSearch = useCallback(() => {
    setChipPage(1);
    setAppliedChipSearch(chipSearch);
  }, [chipSearch]);

  const handleChipStatusChange = useCallback((v) => {
    setChipStatus(v);
    setChipPage(1);
  }, []);

  const handlePlanDraftChange = useCallback((key, field, value) => {
    setPlanDrafts((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  }, []);

  const changePlan = async (userId, plan) => {
    try {
      await api.patch(`/admin/users/${userId}/plan`, { plan });
      toast.success("Plano atualizado");
      fetchUsers(userPage, appliedUserSearch);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleBan = (user) => {
    const novo = user.status === "banido" ? "ativo" : "banido";
    setConfirm({
      title: novo === "banido" ? `Banir ${user.name}?` : `Reativar ${user.name}?`,
      message: novo === "banido"
        ? "A conta será bloqueada e todos os loops dos chips dela serão pausados imediatamente."
        : "A conta volta a operar e os chips retomam o aquecimento.",
      confirmLabel: novo === "banido" ? "Banir conta" : "Reativar conta",
      danger: novo === "banido",
      action: async () => {
        await api.patch(`/admin/users/${user.id}/status`, { status: novo });
        toast.success(novo === "banido" ? "Conta banida e loops pausados" : "Conta reativada");
        fetchUsers(userPage, appliedUserSearch);
      },
    });
  };

  const deleteUser = (user) => {
    setConfirm({
      title: `Excluir ${user.name} permanentemente?`,
      message: `Esta ação é irreversível: a conta ${user.email}, seus chips (incluindo instâncias na Evolution API), mensagens, pagamentos e logs serão removidos da base.`,
      confirmLabel: "Excluir permanentemente",
      danger: true,
      action: async () => {
        await api.delete(`/admin/users/${user.id}`);
        toast.success("Usuário excluído permanentemente");
        fetchUsers(userPage, appliedUserSearch);
        fetchMetricsLogs();
      },
    });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      await confirm.action();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setConfirmLoading(false);
      setConfirm(null);
    }
  };

  const openGrant = useCallback((u) => {
    setGrantUser(u);
    setGrantPlan(u.plan && PLAN_OPTIONS.some((p) => p.value === u.plan) ? u.plan : "m5");
    setGrantDays("7");
  }, []);

  const grantAccess = async () => {
    if (!grantUser) return;
    setGranting(true);
    try {
      await api.post(`/admin/users/${grantUser.id}/grant-access`, { plan: grantPlan, trial_days: Number(grantDays) || 0 });
      toast.success(`Acesso ${Number(grantDays) > 0 ? `de teste (${grantDays}d)` : "cortesia (sem vencimento)"} concedido a ${grantUser.name}`);
      setGrantUser(null);
      fetchUsers(userPage, appliedUserSearch);
      fetchMetricsLogs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGranting(false);
    }
  };

  const saveLoop = async () => {
    setSavingLoop(true);
    try {
      const { data } = await api.put("/admin/loop", {
        loop_master: loop.loop_master,
        schedule_enabled: loop.schedule_enabled,
        schedule_start: loop.schedule_start,
        schedule_end: loop.schedule_end,
        schedule_target: loop.schedule_target,
        schedule_user_ids: loop.schedule_user_ids,
        cadence_delay_min: Number(loop.cadence_delay_min),
        cadence_delay_max: Number(loop.cadence_delay_max),
        typing_min: Number(loop.typing_min),
        typing_max: Number(loop.typing_max),
        human_read_enabled: loop.human_read_enabled,
        human_reply_enabled: loop.human_reply_enabled,
        human_reply_prob: Number(loop.human_reply_prob),
        human_react_enabled: loop.human_react_enabled,
        human_react_prob: Number(loop.human_react_prob),
        human_poll_enabled: loop.human_poll_enabled,
        human_poll_per_group_day: Number(loop.human_poll_per_group_day),
        human_profile_enabled: loop.human_profile_enabled,
        human_sticker_enabled: loop.human_sticker_enabled,
        human_sticker_prob: Number(loop.human_sticker_prob),
        human_edit_enabled: loop.human_edit_enabled,
        human_edit_prob: Number(loop.human_edit_prob),
      });
      setLoop(data);
      toast.success("Configuração global do loop aplicada a todos os chips");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingLoop(false);
    }
  };

  const clearLogs = async () => {
    try {
      await api.delete("/admin/logs");
      toast.success("Logs limpos");
      fetchMetricsLogs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const savePlan = async (key) => {
    const d = planDrafts[key];
    if (!d) return;
    setSavingPlan(key);
    try {
      await api.patch(`/admin/plans/${key}`, { price: Number(d.price), chip_limit: Number(d.chip_limit) });
      toast.success("Plano atualizado — o checkout já reflete o novo valor");
      fetchStatic();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingPlan(null);
    }
  };

  const addGrupo = async () => {
    if (grupoNome.trim().length < 2 || grupoLink.trim().length < 8) {
      toast.error("Informe o nome e o link de convite do grupo");
      return;
    }
    setAddingGrupo(true);
    try {
      await api.post("/admin/grupos", { nome: grupoNome.trim(), link: grupoLink.trim() });
      setGrupoNome("");
      setGrupoLink("");
      toast.success("Grupo cadastrado — os chips conectados já entraram nele");
      fetchStatic();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddingGrupo(false);
    }
  };

  const deleteGrupo = (g) => {
    setConfirm({
      title: `Remover o grupo "${g.nome}"?`,
      message: "Os chips sairão do grupo e ele deixará de receber mensagens do motor.",
      confirmLabel: "Remover grupo",
      danger: true,
      action: async () => {
        await api.delete(`/admin/grupos/${g.id}`);
        toast.success("Grupo removido");
        fetchStatic();
      },
    });
  };

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Área Administrativa</h1>
        <p className="text-sm text-slate-400 mt-1">Visão global consolidada de todos os clientes, chips e o loop de maturação da plataforma.</p>
      </div>

      <AdminMetrics metrics={metrics} />

      <LoopControl loop={loop} setLoop={setLoop} allClients={allClients} saving={savingLoop} onSave={saveLoop} metrics={metrics} />

      <UsersPanel
        users={users}
        search={userSearch}
        onSearchChange={setUserSearch}
        onSearchSubmit={submitUserSearch}
        onPage={setUserPage}
        onChangePlan={changePlan}
        onToggleBan={toggleBan}
        onDelete={deleteUser}
        onGrant={openGrant}
      />

      <ChipsMonitor
        chips={chips}
        metrics={metrics}
        search={chipSearch}
        onSearchChange={setChipSearch}
        onSearchSubmit={submitChipSearch}
        status={chipStatus}
        onStatusChange={handleChipStatusChange}
        onPage={setChipPage}
      />

      <PlansEditor plans={plans} drafts={planDrafts} onDraftChange={handlePlanDraftChange} savingKey={savingPlan} onSave={savePlan} />

      <GruposPanel
        grupos={grupos}
        nome={grupoNome}
        onNomeChange={setGrupoNome}
        link={grupoLink}
        onLinkChange={setGrupoLink}
        adding={addingGrupo}
        onAdd={addGrupo}
        onDelete={deleteGrupo}
      />

      <LogsPanel logs={logs} onClear={clearLogs} />

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirm}
        title={confirm?.title || ""}
        message={confirm?.message || ""}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger ?? true}
        loading={confirmLoading}
        testid="admin-confirm-modal"
      />

      <GrantModal
        user={grantUser}
        plan={grantPlan}
        onPlanChange={setGrantPlan}
        days={grantDays}
        onDaysChange={setGrantDays}
        granting={granting}
        onConfirm={grantAccess}
        onClose={() => setGrantUser(null)}
      />
    </div>
  );
}
